import { Form } from '@hb/finance-ui';
import { Tab, Tabs } from 'vant';
import {
  Component, Mixins, Prop,
} from 'vue-property-decorator';
import { Button } from '@hb/coffee';
import FindCardTips from '@/components/find-card-tips';
import { UploadTypeEnum } from '@/shared/tools/ocr/enum';
import { VerifySceneEnum, VerifyStageEnum } from '@/shared/verify-credit-controller/types';
import platform from '@/shared/utils/platform';
import { getPresentationConfig } from '@/shared/verify-credit-controller';
import IdentityMixins from './identityMixin';
import CreditFlowHeader from '../../credit-flow-header';

import style from './index.module.scss';

@Component
export default class BaseIdentity extends Mixins(IdentityMixins) {
  /** 当前是否“最后一步” */
  @Prop(Boolean) readonly processIsLastVerify!: boolean;

  @Prop(String) readonly verifyScene!: VerifySceneEnum;

  @Prop(Boolean) readonly showSaveConfirm!: boolean;

  @Prop(Boolean) readonly ocrAfterLoan!: boolean;

  @Prop(Boolean) readonly isLoanProcess!: boolean;

  // 底部按钮文案
  private get confirmText() {
    const presentationConfig = getPresentationConfig()[VerifyStageEnum.ID_CARD];

    if (presentationConfig && presentationConfig.submitText) {
      return presentationConfig.submitText;
    }

    if (this.showSaveConfirm) {
      return '保存';
    }

    /** 若是最后一步，则显示"提交申请"按钮 */
    return this.processIsLastVerify ? '提交申请' : `下一步${this.ocrAfterLoan ? '' : ' 人脸验证'}`;
  }

  private renderUploadIdentify() {
    return (
      <Tabs
        lineWidth='20'
        lineHeight='3'
        class={style.tab}
        active={this.identity.uploadType}
        onChange={this.changeUploadType.bind(this)}>
        <Tab name={UploadTypeEnum.Camera}>
          <div class={[style.tabTitle, style.frontTitle, style.left]} slot="title">
            拍摄身份证
          </div>
        </Tab>
        <Tab name={UploadTypeEnum.Album}>
          <div class={[style.tabTitle, style.right]} slot="title">
            从相册上传
          </div>
        </Tab>
      </Tabs>);
  }

  private render() {
    return (
      <div class={[style.identitySimplifyPage, style.container]}>
        <div class={style.contentBox}>
          {/* 头部提示文案：完成身份认证 最高可借20万 */}
          <CreditFlowHeader
            verifyScene={this.verifyScene}
            showLoanTitle={this.isLoanProcess && this.ocrAfterLoan}
          />
          <div
            class={[
              style.pageContainer,
              this.isButtonsVisible && style.withButton,
              this.isOcrSucc && style.withOcrSucc,
            ]}>
            <section class={[style.photoSection, style.identityPhotoSection]}>
              {/* 上传身份证Tab，'拍摄身份证' 或 '从相册上传' */}
              <header>
                {this.isBroswerH5 && !platform.isWechatMini
                  ? (<div class={style.topTitle}>请拍摄本人身份证</div>)
                  : this.renderUploadIdentify()}
              </header>

              {/* 上传身份证照片，'点击拍摄人像面' 或 '点击拍摄国徽面' */}
              <div class={style.mainContent}>
                {this.renderPhotoItem(true, false)}
                {this.renderPhotoItem(false, false)}
              </div>

              {/* 身份证照片识别成功之后的信息回显，包括 姓名、身份证号、有效期限 */}
              {this.hasIdentityData && (
                <section class={style.ocrSection}>
                  {(this.identity.idCardFrontUrl || this.identity.idCardBackUrl)
                    && <div class={style.headerContainer}>
                      识别成功！请核对身份信息，如有误请更正
                    </div>}

                  <div class={style.formContainer}>
                    <Form
                      ref='form'
                      onSubmit={this.onSubmit.bind(this)}
                    >
                      {this.getNormalField('userName', '姓名')}
                      {this.getNormalField('idCardNo', '身份证号')}
                      {this.getNormalField('userSex', '性别', false)}
                      {this.getNormalField('userNation', '民族', false)}
                      {this.getNormalField('userAddress', '住址', false)}
                      {this.getNormalField('issues', '签发机关', false)}
                      {this.getNormalField('periodBegin', '有效期限')}
                    </Form>
                  </div>
                </section>
              )}
            </section>

            <div class={style.requirements}>
              <div class={style.header}>
                <span class={style.title}>拍摄照片要求</span>
              </div>
              <div class={style.requireList}>
                <span class={style.requireItem}>正确示例</span>
                <span class={style.requireItem}>边缘缺失</span>
                <span class={style.requireItem}>照片模糊</span>
                <span class={style.requireItem}>闪光强烈</span>
              </div>
            </div>

            {/* 身份证正面或反面有任意一张照片无值，就显示该提示文案 */}
            {/* 身份证不在身边及提示下载路径 */}
            {(!this.frontIdCard || !this.backIdCard) && (
              <FindCardTips
                visible={this.isFindCardPopupVisible}
                class={style.findCardTips}
                onClose={() => this.onFindCardPopupShow(false)}
                onConfirm={() => this.onFindCardPopupShow(false)}
                onClickTips={() => this.onFindCardPopupShow(true)}
              />
            )}

            {/* 身份证照片识别失败提示弹窗 */}
            {this.renderErrDialog(false)}
            {/* 身份证照片信息失效或清晰分不足，更新身份证提示弹窗 */}
            {this.getUpdatedIdentityPhotoDialog()}
            {/* 切换身份证上传方式确认 */}
            {this.uploadCheckDialog()}
          </div>
        </div>

        {/* 底部操作按钮，'下一步 人脸验证' 或 '保存' */}
        {/* 如果身份认证流程是最后一步，则显示 '保存' 按钮 */}
        {this.isButtonsVisible && (
          <div class={style.buttonContainer}>
            <div class={[style.buttonGroup]}>
              <Button
                block
                onClick={this.onSubmitButtonClick.bind(this)}
                class={style.submitButton}
              >
                {this.confirmText}
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  }
}
